import React, { useEffect, useState } from 'react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  Building,
  Users,
  ClipboardList,
  Map,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Filter,
  Download,
} from 'lucide-react';
import { Select, Button, DatePicker, Dropdown, Menu } from 'antd';
import { getSuperAdminDashboard, getPlantsForFilter } from '../../services/superAdminAPI';
import reportService from '../../services/reportService';
import toast from 'react-hot-toast';

const { RangePicker } = DatePicker;

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const SuperAdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    statistics: {},
    plantStats: [],
    charts: {},
    recentActivity: {},
    safetyMetrics: {},
  });
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    plantId: 'all',
    timeframe: '30',
    dateRange: null,
  });

  useEffect(() => {
    fetchDashboardData();
    fetchPlants();
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [filters.plantId, filters.timeframe]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const data = await getSuperAdminDashboard(filters);
      setDashboardData(data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlants = async () => {
    try {
      const plantsData = await getPlantsForFilter();
      setPlants(plantsData);
    } catch (error) {
      console.error('Error fetching plants:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleReportDownload = async (format) => {
    try {
      const reportData = {
        items: dashboardData.recentActivity.recentObservations || [],
        statistics: dashboardData.statistics,
        chartData: dashboardData.charts,
      };
      
      const selectedPlant = plants.find(p => p._id === filters.plantId);
      const reportFilters = {
        plantName: selectedPlant?.name || 'All Plants',
        dateRange: filters.dateRange ? 
          `${filters.dateRange[0].format('YYYY-MM-DD')} to ${filters.dateRange[1].format('YYYY-MM-DD')}` :
          `Last ${filters.timeframe} days`,
      };

      let result;
      switch (format) {
        case 'pdf':
          result = await reportService.generatePDF(reportData, 'observations', reportFilters);
          break;
        case 'excel':
          result = await reportService.generateExcel(reportData, 'observations', reportFilters);
          break;
        case 'word':
          result = await reportService.generateWord(reportData, 'observations', reportFilters);
          break;
        default:
          throw new Error('Unsupported format');
      }

      if (result.success) {
        toast.success(`${format.toUpperCase()} report downloaded successfully!`);
      }
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error(`Failed to generate ${format.toUpperCase()} report`);
    }
  };

  const downloadMenu = (
    <Menu onClick={({ key }) => handleReportDownload(key)}>
      <Menu.Item key="pdf">Download PDF</Menu.Item>
      <Menu.Item key="excel">Download Excel</Menu.Item>
      <Menu.Item key="word">Download Word</Menu.Item>
    </Menu>
  );

  // Chart configurations
  const observationTypeData = {
    labels: ['Unsafe Act', 'Unsafe Condition'],
    datasets: [
      {
        data: [
          dashboardData?.charts?.observationsByType?.find(item => item._id === 'unsafe act')?.count || 0,
          dashboardData?.charts?.observationsByType?.find(item => item._id === 'unsafe condition')?.count || 0,
        ],
        backgroundColor: ['#F59E0B', '#EF4444'],
        borderColor: ['#D97706', '#B91C1C'],
        borderWidth: 2,
      },
    ],
  };

  const statusData = {
    labels: ['Waiting', 'In Progress', 'Submitted', 'Completed'],
    datasets: [
      {
        data: [
          dashboardData?.charts?.observationsByStatus?.find(item => item._id === 'waiting')?.count || 0,
          dashboardData?.charts?.observationsByStatus?.find(item => item._id === 'in-progress')?.count || 0,
          dashboardData?.charts?.observationsByStatus?.find(item => item._id === 'submitted')?.count || 0,
          dashboardData?.charts?.observationsByStatus?.find(item => item._id === 'completed')?.count || 0,
        ],
        backgroundColor: ['#F59E0B', '#9CA3AF', '#3B82F6', '#10B981'],
        borderColor: ['#D97706', '#6B7280', '#2563EB', '#059669'],
        borderWidth: 2,
      },
    ],
  };

  const monthlyTrendsData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Observations',
        data: Array.from({ length: 12 }, (_, i) => 
          dashboardData?.charts?.monthlyTrends?.find(item => item._id === i + 1)?.count || 0
        ),
        borderColor: '#3B82F6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
      },
    ],
  };

  const plantPerformanceData = {
    labels: dashboardData?.charts?.topPerformingPlants?.map(plant => plant.name) || [],
    datasets: [
      {
        label: 'Completion Rate (%)',
        data: dashboardData?.charts?.topPerformingPlants?.map(plant => plant.completionRate) || [],
        backgroundColor: 'rgba(34, 197, 94, 0.6)',
        borderColor: 'rgba(34, 197, 94, 1)',
        borderWidth: 1,
      },
    ],
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Super Admin Dashboard</h1>
            <p className="text-gray-600 mt-2">
              Comprehensive overview of all plants and safety operations
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
            <Dropdown overlay={downloadMenu} placement="bottomRight">
              <Button type="primary" icon={<Download size={16} />}>
                Download Report
              </Button>
            </Dropdown>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow-md">
          <div className="flex flex-col lg:flex-row gap-4 items-end">
            <div className="flex items-center gap-2">
              <Filter size={16} className="text-gray-500" />
              <span className="font-medium text-gray-700">Filters:</span>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              <Select
                value={filters.plantId}
                onChange={(value) => handleFilterChange('plantId', value)}
                placeholder="Select Plant"
                className="min-w-48"
                showSearch
                optionFilterProp="children"
              >
                <Select.Option value="all">All Plants</Select.Option>
                {plants?.map(plant => (
                  <Select.Option key={plant._id} value={plant._id}>
                    {plant.name} - {plant.location}
                  </Select.Option>
                ))}
              </Select>
              
              <Select
                value={filters.timeframe}
                onChange={(value) => handleFilterChange('timeframe', value)}
                className="min-w-40"
              >
                <Select.Option value="7">Last 7 days</Select.Option>
                <Select.Option value="30">Last 30 days</Select.Option>
                <Select.Option value="90">Last 3 months</Select.Option>
                <Select.Option value="365">Last year</Select.Option>
              </Select>
              
              <RangePicker
                onChange={(dates) => handleFilterChange('dateRange', dates)}
                placeholder={['Start Date', 'End Date']}
              />
            </div>
          </div>
        </div>

        {/* Key Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            icon={<Building className="text-blue-600" />}
            label="Total Plants"
            value={dashboardData?.statistics?.totalPlants || 0}
            bg="bg-blue-100"
          />
          <StatCard
            icon={<Users className="text-green-600" />}
            label="Total Users"
            value={dashboardData?.statistics?.totalUsers || 0}
            bg="bg-green-100"
          />
          <StatCard
            icon={<ClipboardList className="text-purple-600" />}
            label="Total Observations"
            value={dashboardData?.statistics?.totalObservations || 0}
            bg="bg-purple-100"
          />
          <StatCard
            icon={<Map className="text-orange-600" />}
            label="Active Areas"
            value={dashboardData?.statistics.totalAreas || 0}
            bg="bg-orange-100"
          />
        </div>

        {/* Safety Metrics */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <TrendingUp className="mr-2 text-green-600" />
            Safety Performance Metrics
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {dashboardData?.safetyMetrics.resolutionRate || 0}%
              </div>
              <div className="text-sm text-gray-600">Resolution Rate</div>
            </div>
            
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                {dashboardData?.safetyMetrics.avgResolutionTime || 0}
              </div>
              <div className="text-sm text-gray-600">Avg Resolution Days</div>
            </div>
            
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">
                {dashboardData?.safetyMetrics.totalIncidents || 0}
              </div>
              <div className="text-sm text-gray-600">Total Incidents</div>
            </div>
            
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">
                {dashboardData?.safetyMetrics.criticalObservations || 0}
              </div>
              <div className="text-sm text-gray-600">Critical Issues</div>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartCard title="Observation Types" chart={<Doughnut data={observationTypeData} />} />
          <ChartCard title="Status Distribution" chart={<Doughnut data={statusData} />} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartCard title="Monthly Trends" chart={<Line data={monthlyTrendsData} />} />
          <ChartCard title="Plant Performance" chart={<Bar data={plantPerformanceData} />} />
        </div>

        {/* Plant Statistics Table */}
        {dashboardData?.plantStats && dashboardData?.plantStats.length > 0 && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold">Plant Performance Overview</h2>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Plant Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Users
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Observations
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {dashboardData?.plantStats?.map((plant, index) => (
                    <tr key={plant._id || index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {plant.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {plant.location}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {plant.userCount || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {plant.observationCount || 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Recent Activity */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-4">Recent Activity</h2>
          
          <div className="space-y-4">
            {dashboardData?.recentActivity?.recentObservations?.map((activity, index) => (
              <div
                key={index}
                className={`border-l-4 pl-4 py-2 ${
                  activity?.observationType === 'unsafe act' ? 'border-amber-500' : 'border-red-500'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium">
                      New {activity.observationType} observation
                    </p>
                    <p className="text-sm text-gray-500">
                      {activity.area} • {activity.plant} • By {activity.user}
                    </p>
                  </div>
                  <span className="text-sm text-gray-400">
                    {new Date(activity.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}

            {dashboardData?.recentActivity?.recentUsers?.map((user, index) => (
              <div key={index} className="border-l-4 border-green-500 pl-4 py-2">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium">New user joined</p>
                    <p className="text-sm text-gray-500">
                      {user.name} ({user.accountType}) • {user.plant}
                    </p>
                  </div>
                  <span className="text-sm text-gray-400">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon, label, value, bg }) => (
  <div className="bg-white p-6 rounded-lg shadow-md flex items-center">
    <div className={`rounded-full ${bg} p-3 mr-4`}>{icon}</div>
    <div>
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-2xl font-semibold">{value}</p>
    </div>
  </div>
);

const ChartCard = ({ title, chart }) => (
  <div className="bg-white p-6 rounded-lg shadow-md">
    <h2 className="text-lg font-semibold mb-4">{title}</h2>
    <div className="h-64 flex items-center justify-center">
      {chart}
    </div>
  </div>
);

export default SuperAdminDashboard;